import React, { memo, useContext, useMemo } from "react";
import Navbar from "app/components/Navbar";
import { cls } from "app/utils";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import GettingStartedProgress from "app/components/GettingStarted/GettingStartedProgress";
import SetupChecklist from "app/components/AcademySetup/SetupChecklist";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { hasAcademyPlan, isAcademyOnboardingCoach } from "app/utils/subscription";
import Tabs from "app/components/Tabs";
import { UserContext, RoleType } from "app/contexts/UserContext";

interface IProps {
  children: React.ReactNode;
}

const CoachHomeLayout = ({ children }: IProps) => {
  const { t } = useTranslation();

  const { self } = useSelector((state: RootState) => state.user);
  const user = useContext(UserContext);

  const academyPlan = hasAcademyPlan(self);
  const isAdmin = user.role === RoleType.ADMIN;
  // Same gate as SetupChecklist, so banner and checklist agree on who is
  // onboarding. Facility staff on the Academy plan get neither surface: the
  // legacy banner hides itself on the plan.
  const academyChecklist = isAcademyOnboardingCoach(self);

  const tabs = useMemo(() => {
    // Admin users only see Contacts tab
    if (isAdmin) {
      return [
        {
          label: t("Contacts"),
          path: "/contacts",
          permission: ["contacts:view"],
        },
      ];
    }

    const allTabs = [
      { label: t("Spaces"), path: "/spaces" },
      { label: t("Groups"), path: "/groups" },
      { label: t("Lists"), path: "/lists" },
      {
        label: t("Contacts"),
        path: "/contacts",
        permission: ["contacts:view"],
        condition: academyPlan,
      },
    ];

    return allTabs.filter((tab) => {
      const conditionOk = tab.condition === undefined || tab.condition;
      return conditionOk;
    });
  }, [self, academyPlan, isAdmin, t]);

  return (
    <div>
      <Navbar
        label={t("Home")}
        hasSearch={!isAdmin}
        hasNotifications={!isAdmin}
      />
      <div className={cls(styles.container, "coach-home")}>
        {!isAdmin &&
          (academyChecklist ? (
            <SetupChecklist banner />
          ) : (
            <GettingStartedProgress />
          ))}
        <Tabs tabs={tabs} />
        {children}
      </div>
    </div>
  );
};

export default memo(CoachHomeLayout);
